import naturalSort from 'natural-sort';
import * as R from 'ramda';

import { isNilOrEmpty } from '@atom/utilities/validationUtilities';

import { schema } from './collinsMpcConstants';

export const getAttributeValueById = (attributes: any, attributeId: string) => {
  if (isNilOrEmpty(attributes) || isNilOrEmpty(attributeId)) {
    return null;
  }

  return R.pathOr(null, [attributeId, 'value'], attributes);
};

export const getAttributeDataRows = (
  attributes: any,
  displayOrder: string[],
) => {
  if (isNilOrEmpty(attributes) || isNilOrEmpty(displayOrder)) {
    return [];
  }

  return displayOrder.reduce((acc, attributeId) => {
    const attribute = attributes[attributeId];

    return isNilOrEmpty(attribute?.value)
      ? acc
      : [...acc, { id: attributeId, ...attribute }];
  }, []);
};

export const sortElementByNameComparator = (firstElement, secondElement) => {
  if (!firstElement.name || !secondElement.name) {
    return 0;
  }

  const names = [firstElement.name, secondElement.name];
  const sortedNames = names.sort(naturalSort());

  return firstElement.name === sortedNames[0] ? -1 : 1;
};

export const getFlatElementList = workOrder => {
  if (isNilOrEmpty(workOrder)) {
    return [];
  }

  const mainAsset = workOrder.assets[workOrder.inventoryAssetId];

  const topLevelElements = mainAsset.elements.reduce((acc, element) => {
    return [
      ...acc,
      ...element.elements.map(elem => {
        return { ...workOrder.assets[elem.id], topLevel: true };
      }),
    ];
  }, []);

  const topLevelElementsSorted = [...topLevelElements].sort(
    sortElementByNameComparator,
  );

  const fullElementList = topLevelElementsSorted.reduce((acc, element) => {
    const nestedElements = element.elements.reduce((accumulator, elem) => {
      return [
        ...accumulator,
        ...elem.elements.map(innerElement => {
          return {
            ...workOrder.assets[innerElement.id],
            name: `${element.name} / ${innerElement.name}`,
          };
        }),
      ];
    }, []);

    const sortedNestedElements = [...nestedElements].sort(
      sortElementByNameComparator,
    );

    return [...acc, element, ...sortedNestedElements];
  }, []);

  return fullElementList;
};

export const getRepairRecommendationsList = workOrder => {
  const flatElementList = getFlatElementList(workOrder);

  return flatElementList.reduce((acc, element) => {
    const repairRecommendationId = R.intersection(
      R.keys(element.attributes),
      schema.poolAttributes.repairRecommendation,
    );

    if (!R.isNil(element.attributes[schema.poolAttributes.actionRequired])) {
      return element.attributes[schema.poolAttributes.actionRequired].value ===
        'Yes'
        ? [
            ...acc,
            {
              quantity: R.pathOr(
                '',
                ['attributes', schema.poolAttributes.quantity, 'value'],
                element,
              ),
              unit: R.pathOr(
                '',
                ['attributes', schema.poolAttributes.unit, 'value'],
                element,
              ),
              repairPriority: R.pathOr(
                '',
                ['attributes', schema.poolAttributes.repairPriority, 'value'],
                element,
              ),
              repairRecommendation: R.pathOr(
                '',
                // @ts-ignore
                ['attributes', repairRecommendationId, 'value'],
                element,
              ),
            },
          ]
        : acc;
    }
    return acc;
  }, []);
};

export const getGeneralDataType = workOrderAsset => {
  if (isNilOrEmpty(workOrderAsset)) {
    return '';
  }

  return R.pathOr(
    '',
    ['attributes', schema.generalData.type, 'value'],
    workOrderAsset,
  );
};

export const getNestedElementDisplayRows = (attributes, media) => {
  if (isNilOrEmpty(attributes) && isNilOrEmpty(media)) {
    return [];
  }

  // creates mixed array of attribute object and media objects
  const mixedArray = [
    ...(isNilOrEmpty(attributes) ? [] : [{ isMedia: false, attributes }]),
    ...(isNilOrEmpty(media)
      ? []
      : media.map(item => ({ ...item, isMedia: true }))),
  ];

  // creates and array of arrays with 2 items in each
  const chunkedArray = mixedArray.reduce((resultArray, item, index) => {
    const chunkIndex = Math.floor(index / 2);
    if (!resultArray[chunkIndex]) {
      resultArray[chunkIndex] = [];
    }
    resultArray[chunkIndex].push(item);
    return resultArray;
  }, []);

  return chunkedArray;
};
